// dependencies
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { toast } from "@performyard/py-components";

// components
import Button from "../../../../../../../../../components/Button";
import DatePicker from "../../../../../../../../../components/Inputs/DatePicker";
import { BaseModalProps } from "../../../../../../../../../components/Modal/BaseModal";
import ContentModal from "../../../../../../../../../components/Modal/Templates/ContentModal";

// hooks
import { useGoalDetails, useGoalHistory, useMutatePatchGoalHistory } from "../../../../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../../../../hooks/useSearchQueryParams";

// store
import { Goal, GoalHistory } from "../../../../../../../../../api/services";

//confg
import Config, { MIN_AS_OF_DATE, MIN_AS_OF_DATE_ERROR } from "../../../../../../config";
// styles
import css from "../../../../ActivityStreamPanel.module.scss";

const GoalProgressDateModal = ({ isOpen, handleClose }: BaseModalProps) => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const [date, setDate] = useState(new Date());

	const {
		queryParams: { editHistoryEntryId },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);
	const { data: progressHistory = [], refetch: refetchProgress } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
	});

	const entry = progressHistory?.find((entry: GoalHistory) => editHistoryEntryId === entry._id);

	useEffect(() => {
		setDate(entry?.as_of || new Date());
	}, [isOpen]);

	const { mutate: editEntry, isPending } = useMutatePatchGoalHistory({
		onSuccess: () => {
			refetchGoal();
			refetchActivity();
			refetchProgress();
			handleQueryChange({ modal: undefined, editHistoryEntryId: undefined });
			handleClose();
		},
		onError: (errors) => {
			toast.error(errors[0] || "Internal Error");
		},
	});

	const isDisabled = !date || date > new Date() || date < MIN_AS_OF_DATE || goal?.status !== "active";

	const onSubmit = () => {
		if (!isDisabled) {
			editEntry({ goalID: goal?._id || "", data: { as_of: date?.toISOString(), goal_history_id: entry?._id } });
		}
	};

	let errorMessage = "";

	if (!date) {
		errorMessage = "Please select a date";
	} else if (date > new Date()) {
		errorMessage = "Date cannot be in the future";
	} else if (date < MIN_AS_OF_DATE) {
		errorMessage = MIN_AS_OF_DATE_ERROR;
	}

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={handleClose}
			title="Edit As of Date"
			buttons={[
				<Button
					key="cancel-date-modal"
					isHollow={true}
					onClick={handleClose}
					data-testid={`cancel-date-modal-${entry?._id}`}
				>
					Cancel
				</Button>,
				<Button
					key="update-date-modal"
					data-testid={`update-date-modal-${entry?._id}`}
					isRequesting={isPending}
					onClick={onSubmit}
					disabled={isDisabled}
					color="green"
				>
					Update
				</Button>,
			]}
		>
			<div className={css.editAsOfModal}>
				<DatePicker
					value={date}
					onChange={(value) => value && setDate(value)}
					label="As of Date"
					hasIcon
					errorMessage={errorMessage}
				/>
			</div>
		</ContentModal>
	);
};

export default GoalProgressDateModal;
